import { PlusIcon, ArrowPathIcon } from '@heroicons/react/24/outline'
import { useTranslation } from 'react-i18next'

/**
 * MealPlaceholder Component
 *
 * Displays a placeholder for unlogged meals with a call-to-action button.
 *
 * VH-141 adds a second action: "same as last time". Reto eats the same
 * breakfast, lunch and dinner most weekdays, so re-typing them is the cost this
 * removes. It lives HERE, in the empty slot, because that is where he already
 * taps to log that meal — the shortcut sits on the path rather than beside it,
 * and it disappears the moment the slot is filled.
 *
 * The source date is always named. It is the most recent earlier day that has
 * the meal, not necessarily yesterday, so "yesterday" would sometimes be a lie.
 *
 * Structural note: this used to be one big `<button>`. A copy button cannot nest
 * inside another button, so the wrapper is now a div with two real buttons.
 */
export default function MealPlaceholder({
  mealType = 'LUNCH',
  onLogMeal = () => {},
  copySource = null,
  onCopyMeal = null,
  isCopying = false
}) {
  const { t, i18n } = useTranslation()

  // Meal type configuration
  const mealConfig = {
    BREAKFAST: { icon: '🌅', color: 'text-amber-600', bgColor: 'bg-amber-50', borderColor: 'border-amber-200', hoverBg: 'hover:bg-amber-100' },
    LUNCH: { icon: '🌞', color: 'text-yellow-600', bgColor: 'bg-yellow-50', borderColor: 'border-yellow-200', hoverBg: 'hover:bg-yellow-100' },
    DINNER: { icon: '🌙', color: 'text-indigo-600', bgColor: 'bg-indigo-50', borderColor: 'border-indigo-200', hoverBg: 'hover:bg-indigo-100' },
    SNACK: { icon: '🍎', color: 'text-green-600', bgColor: 'bg-green-50', borderColor: 'border-green-200', hoverBg: 'hover:bg-green-100' }
  }

  const config = mealConfig[mealType] || mealConfig.LUNCH

  const typeKey = mealType.toLowerCase()
  const typeLabel = t(`nutrition.mealTypes.${typeKey}`, {
    defaultValue: mealType.charAt(0) + mealType.slice(1).toLowerCase()
  })

  // Named explicitly rather than as "yesterday": the source is the most recent
  // earlier day that HAS this meal, which is often not yesterday.
  const sourceLabel = copySource?.source_date
    ? new Date(`${copySource.source_date}T00:00:00`).toLocaleDateString(i18n.language || 'en', {
        weekday: 'short',
        day: 'numeric',
        month: 'short'
      })
    : null

  const canCopy = Boolean(onCopyMeal && copySource?.source_date && copySource?.meal_count > 0)

  return (
    <div className="space-y-1">
      <button
        onClick={() => onLogMeal(mealType)}
        className={`w-full ${config.bgColor} ${config.borderColor} border-2 border-dashed rounded-lg p-6 transition-all duration-200 ${config.hoverBg} hover:border-solid hover:shadow-md group`}
      >
        <div className="flex items-center justify-center space-x-3">
          <span className="text-3xl transition-transform group-hover:scale-110">{config.icon}</span>
          <div className="text-left">
            <div className={`text-lg font-semibold ${config.color}`}>
              {t('nutrition.placeholder.logMeal', { meal: typeLabel })}
            </div>
            <div className="text-sm text-gray-500">
              {t('nutrition.placeholder.clickToAdd')}
            </div>
          </div>
          <PlusIcon className={`h-6 w-6 ${config.color} ml-auto transition-transform group-hover:rotate-90`} />
        </div>
      </button>

      {canCopy && (
        <button
          type="button"
          onClick={() => onCopyMeal(mealType, copySource.source_date)}
          disabled={isCopying}
          data-testid={`copy-meal-${typeKey}`}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-on-surface-variant border border-outline bg-surface-container transition-colors hover:text-on-surface disabled:opacity-60 disabled:cursor-not-allowed"
        >
          <ArrowPathIcon className={`h-4 w-4 ${isCopying ? 'animate-spin' : ''}`} />
          <span>
            {isCopying
              ? t('nutrition.placeholder.copying')
              : copySource.meal_count > 1
                ? t('nutrition.placeholder.copyFromDateMultiple', {
                    date: sourceLabel,
                    count: copySource.meal_count
                  })
                : t('nutrition.placeholder.copyFromDate', { date: sourceLabel })}
          </span>
        </button>
      )}
    </div>
  )
}
